<template>
  <div id="tags-view-container" class="tags-view-container">
    <ElTabs v-model="activePath" closable @tab-click="handleTabClick"
            @tab-remove="closeTag">
      <ElTabPane v-for="tag in visitedViews" :label="tag.fullPath" :key="tag.fullPath"
                 :name="tag.fullPath"
      >
        <template #label>
        <span class="pane__content"
              @click.middle="!isAffix(tag) ? closeTag(tag.fullPath) : ''"
              @contextmenu.prevent.stop="openMenu(tag, $event)">{{ tag.title }}
            </span>
        </template>
      </ElTabPane>
    </ElTabs>

    <ul v-show="visible" :style="{ left: left + 'px', top: top + 'px' }" class="contextmenu">
      <li @click="refreshSelectedTag(selectedTag)">
        <refresh-right style="width: 1em; height: 1em;"/>
        刷新页面
      </li>
      <li v-if="!isAffix(selectedTag)" @click="closeTag(selectedTag.fullPath)">
        <close style="width: 1em; height: 1em;"/>
        关闭当前
      </li>
      <li @click="closeOthersTags">
        <circle-close style="width: 1em; height: 1em;"/>
        关闭其他
      </li>
      <li v-if="!isFirstView()" @click="closeLeftTags">
        <back style="width: 1em; height: 1em;"/>
        关闭左侧
      </li>
      <li v-if="!isLastView()" @click="closeRightTags">
        <right style="width: 1em; height: 1em;"/>
        关闭右侧
      </li>
      <li @click="closeAllTags(selectedTag)">
        <circle-close style="width: 1em; height: 1em;"/>
        全部关闭
      </li>
    </ul>
  </div>
</template>

<script setup>
import ScrollPane from './ScrollPane'
import {getNormalPath} from '@/utils/ruoyi'
import useTagsViewStore from '@/store/modules/tagsView'
import useSettingsStore from '@/store/modules/settings'
import usePermissionStore from '@/store/modules/permission'
import { until } from '@vueuse/core'

const visible = ref(false)
const top = ref(0)
const left = ref(0)
const activePath = ref() // 当前页签
const selectedTag = ref({fullPath: ''})
const affixTags = ref([])
const scrollPaneRef = ref(null)

const {proxy} = getCurrentInstance()
const route = useRoute()
const router = useRouter()

const visitedViews = computed(() => useTagsViewStore().visitedViews)
const routes = computed(() => usePermissionStore().routes)
const theme = computed(() => useSettingsStore().theme)

watch(route, () => {
  addTags()
  // moveToCurrentTag()
  setSelectedTag()
})
watch(visible, (value) => {
  if (value) {
    document.body.addEventListener('click', closeMenu)
  } else {
    document.body.removeEventListener('click', closeMenu)
  }
})
onMounted(() => {
  initTags()
  addTags()

  setSelectedTag()
})

// 设置当前选中的页签，activePath跟selectedTag区分开，否则右键页签时就会切换了
function setSelectedTag() {
  if (route.fullPath) {
    const target = visitedViews.value.find(item => item.fullPath === route.fullPath)
    if (target) {
      /* 其他两种拷贝方式报错原因分析:
      * 1.为什么 JSON.parse(JSON.stringify()) 报 cyclic object value？
      * --因为 matched 数组里的对象存在循环引用（例如 parent 指向父级路由，children 指向子级路由），原生 JSON 序列化遇到这种死循环就会直接崩溃。
      * 2.为什么 structuredClone() 报 Function object could not be cloned？
      * --因为 Vue Router 的路由记录对象上挂载了内部的处理方法（比如用于匹配路径、解析参数的函数），而 structuredClone 的底层规范绝对禁止克隆函数。
      *3.为什么 lodash.cloneDeep() 不报错？
      * --因为 lodash 是用 JS 写的，它遇到函数时会保留原函数引用，遇到循环引用时内部会记录已访问的对象从而避免死循环，所以它“宽容”地完成了任务。
      * */
      // selectedTag.value =cloneDeep(toRaw(target))
      selectedTag.value = {...target}
      activePath.value = route.fullPath
    }
  }
}

function isActive(r) {
  return r.path === route.path
}

function isAffix(tag) {
  return tag.meta && tag.meta.affix
}

function isFirstView() {
  try {
    return selectedTag.value.fullPath === '/index' || selectedTag.value.fullPath === visitedViews.value[1].fullPath
  } catch (err) {
    return false
  }
}

function isLastView() {
  try {
    return selectedTag.value.fullPath === visitedViews.value[visitedViews.value.length - 1].fullPath
  } catch (err) {
    return false
  }
}

function filterAffixTags(routes, basePath = '') {
  let tags = []
  routes.forEach(route => {
    if (route.meta && route.meta.affix) {
      const tagPath = getNormalPath(basePath + '/' + route.path)
      tags.push({
        fullPath: tagPath,
        path: tagPath,
        name: route.name,
        meta: {...route.meta}
      })
    }
    if (route.children) {
      const tempTags = filterAffixTags(route.children, route.path)
      if (tempTags.length >= 1) {
        tags = [...tags, ...tempTags]
      }
    }
  })
  return tags
}

function initTags() {
  const res = filterAffixTags(routes.value)
  affixTags.value = res
  for (const tag of res) {
    // Must have tag name
    if (tag.name) {
      useTagsViewStore().addVisitedView(tag)
    }
  }
}

function addTags() {
  const {name} = route
  if (name) {
    useTagsViewStore().addView(route)
    if (route.meta.link) {
      useTagsViewStore().addIframeView(route)
    }
  }
  return false
}

const moveToCurrentTag = async () => {
  await nextTick()
  for (const r of visitedViews.value) {
    if (r.path === route.path) {
      try {
        // 等待 scrollPaneRef.value存在，设置超时为 2秒
        await until(() => scrollPaneRef.value).toBeTruthy({timeout: 2000})
        scrollPaneRef.value.moveToTarget(r)
      } catch (e) {
        console.error('scrollPaneRef 不存在或加载超时，跳过初始化')
      } finally {
        // when query is different then update
        if (r.fullPath !== route.fullPath) {
          useTagsViewStore().updateVisitedView(route)
        }
      }

    }
  }
}

function refreshSelectedTag(view) {
  proxy.$tab.refreshPage(view)
  if (route.meta.link) {
    useTagsViewStore().delIframeView(route)
  }
}

// tabs 关闭页签
const closeTag = (fullPath) => {
  const target = visitedViews.value.find(item => item.fullPath === fullPath)
  if (target) {
    proxy.$tab.closePage(target).then(({visitedViews}) => {
      if (isActive(target)) {
        toLastView(visitedViews, target)
      }
    })
  }
}

const handleTabClick = (pane) => {
  if (!pane?.props?.label) return
  const target = visitedViews.value.find(item => item.fullPath === pane?.props?.label)
  if (target) {
    router.push({path: target.path, query: target.query, fullPath: target.fullPath})
  }
}

function closeRightTags() {
  proxy.$tab.closeRightPage(selectedTag.value).then(visitedViews => {
    if (!visitedViews.find(i => i.fullPath === route.fullPath)) {
      toLastView(visitedViews)
    }
  })
}

function closeLeftTags() {
  proxy.$tab.closeLeftPage(selectedTag.value).then(visitedViews => {
    if (!visitedViews.find(i => i.fullPath === route.fullPath)) {
      toLastView(visitedViews)
    }
  })
}

function closeOthersTags() {
  router.push(selectedTag.value).catch(() => {
  })
  proxy.$tab.closeOtherPage(selectedTag.value).then(() => {
    moveToCurrentTag()
  })
}

function closeAllTags(view) {
  proxy.$tab.closeAllPage().then(({visitedViews}) => {
    if (affixTags.value.some(tag => tag.path === route.path)) {
      return
    }
    toLastView(visitedViews, view)
  })
}

function toLastView(visitedViews, view) {
  const latestView = visitedViews.slice(-1)[0]
  if (latestView) {
    router.push(latestView.fullPath)
  } else {
    // now the default is to redirect to the home page if there is no tags-view,
    // you can adjust it according to your needs.
    if (view.name === 'Dashboard') {
      // to reload home page
      router.replace({path: '/redirect' + view.fullPath})
    } else {
      router.push('/')
    }
  }
}

function openMenu(tag, e) {
  const menuMinWidth = 105
  const offsetLeft = proxy.$el.getBoundingClientRect().left // container margin left
  const offsetWidth = proxy.$el.offsetWidth // container width
  const maxLeft = offsetWidth - menuMinWidth // left boundary
  const l = e.clientX - offsetLeft + 15 // 15: margin right

  if (l > maxLeft) {
    left.value = maxLeft
  } else {
    left.value = l
  }

  top.value = e.clientY
  visible.value = true
  selectedTag.value = tag
}

function closeMenu() {
  visible.value = false
}

function handleScroll() {
  closeMenu()
}
</script>

<style lang='scss'>
.tags-view-container {
  --gray-bg: #f7f9fa;
  height: 38px;
  width: 100%;
  background: #fff;
  overflow: hidden;
  box-shadow: 10px -1px 8px #5c84fe66;

  .el-tabs {
    width: 100%;
    height: inherit;

    .el-tabs__nav-wrap {
      height: inherit;
      margin-bottom: 0;

      &::after {
        height: 1px;
        background-color: var(--el-border-color-light);
      }

      .el-tabs__nav-scroll, .el-tabs__nav {
        height: inherit;
      }
    }

    .el-tabs__content {
      display: none;
    }


    .el-tabs__header {
      margin-bottom: 0;

      .el-tabs__active-bar {
        display: none;
      }

      .el-tabs__item {
        --gray-bg: #f7f9fa;
        height: inherit;
        padding: 8px 0 0;
        margin-left: -14px;
        overflow: hidden;
        position: relative;
        font-size: 12px;
        color: #898989;

        &[aria-controls='pane-/index'] {
          margin-left: 0;

          .el-icon.is-icon-close {
            display: none;
          }

          .pane__content {
            padding-right: 10px;
          }
        }

        &:before {
          position: absolute;
          left: -20px;
          width: 34px;
          height: 37px;
          background: transparent;
          border-radius: 0 0 30px 0;
          box-shadow: 4px 14px 0 0 var(--gray-bg);
          content: '';
          z-index: -1;
        }

        &:after {
          position: absolute;
          right: -20px;
          width: 34px;
          height: 37px;
          background: transparent;
          border-radius: 0 0 0 30px;
          box-shadow: -1px 16px 0 0 var(--gray-bg);
          content: "";
          z-index: -2;
        }

        .pane__content {
          display: inline-block;
          height: 30px;
          padding: 0 26px 0 10px;
          margin: 0 14px;
          line-height: 30px;
          border-radius: 12px 12px 0 0;
          background: var(--gray-bg);
          user-select: none;
        }

        .el-icon.is-icon-close {
          position: absolute;
          right: 18px;
          width: 18px;
          height: 18px;

          &:hover {
            background-color: var(--el-color-warning);
          }
        }

        &:hover {
          color: var(--el-color-primary);
        }

        &.is-active {
          --color-primary-bg-light: #e8ecff;
          color: var(--el-color-primary);
          z-index: 299;

          &:before {
            box-shadow: 4px 14px 0 0 var(--el-color-primary-light-9);
          }

          &:after {
            box-shadow: -1px 16px 0 0 var(--el-color-primary-light-9);
          }

          .pane__content {
            background: var(--el-color-primary-light-9);
          }
        }
      }
    }
  }

  .contextmenu {
    margin: 0;
    background: #fff;
    z-index: 3000;
    position: absolute;
    list-style-type: none;
    padding: 5px 0;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 400;
    color: #333;
    box-shadow: 2px 2px 3px 0 rgba(0, 0, 0, 0.3);

    li {
      margin: 0;
      padding: 7px 16px;
      cursor: pointer;

      &:hover {
        background: #eee;
      }
    }
  }
}
</style>
